import React, { useContext } from 'react';
import Scroll from 'react-scroll';
import cn from 'classnames';
import ConfigContext from '../../../contexts/ConfigContext';

// Components
import LogoList from '../content/LogoList';
import Meta from '../../shared/Meta';
import ServicesForm from '../ServicesForm';
import Triple from '../content/Triple';
import CtaImage from '../content/CtaImage';
import FeaturedTrucks from '../FeaturedTrucks';
import SiteRequirements from '../content/SiteRequirements';
import Testimonials from '../content/Testimonials';
import OfficeFaq from '../faqs/OfficeFaq';

// Misc
import styles from '../../../../stylesheets/public/layouts/AboutPage.module.postcss';
import tripleStyles from '../content/Triple.module.postcss';

const featuredTrucks = { featured: true };

function OfficeParks() {
  const { vehicle_type, city_name } = useContext(ConfigContext);
  const vehicleType = vehicle_type.toLowerCase();

  return (
    <div>
      <Meta
        title='Services'
        breadcrumbName='Services'
      />

      <div className='Section'>
        <div className={cn(styles.Cta, styles.Cta__reversed)}>
          <CtaImage image='https://static.seattlefoodtruck.com/photos/services-header.webp' noLazy isResponsive />
          <div className={styles.Cta_copy}>
            <h1>Food {vehicle_type}s @ Work</h1>
            <p>Offer your employees or tenants a rotating selection of {city_name}&#39;s best food {vehicleType}s — fresh, convenient, and hassle-free. Whether it&#39;s a daily lunch service or a weekly rotation, we handle the scheduling so you can provide great meals at little to no cost to your business.</p>
            <p>
              <Scroll.Link
                smooth
                to='form'
                className='Button Button--mobile-w100'
                offset={-50}
              >
                Schedule Food {vehicle_type}s Now
              </Scroll.Link>
            </p>
          </div>
        </div>
      </div>

      <div className='Section'>
        <LogoList title='Partner with companies like' />
      </div>

      <section className='Section'>
        <h2>How It Works</h2>
        <div className={tripleStyles.Triple_wrapper}>
          <Triple
            image='https://static.seattlefoodtruck.com/photos/services1.webp'
            isResponsive
            title={<><span aria-hidden='true'>📅 </span>Curated Schedule</>}
            text={`Our platform automatically schedules ${city_name}'s best food ${vehicleType}s for your workplace, rotating top-rated vendors based on employee preferences. Enjoy a variety of cuisines from around the world, tailored to your team's tastes.`}
          />
          <Triple
            image='https://static.seattlefoodtruck.com/photos/services2.webp'
            isResponsive
            title={<><span aria-hidden='true'>📩 </span>Fully Automated</>}
            text={`Employees receive weekly menu emails with upcoming food ${vehicleType}s, plus a custom web page to check the lineup anytime. No more guesswork—just great food on a predictable schedule.`}
          />
          <Triple
            image='https://static.seattlefoodtruck.com/photos/services3.webp'
            isResponsive
            title={<><span aria-hidden='true'>✅ </span>Hassle-Free Experience</>}
            text={`Food ${vehicleType}s arrive, serve and sell lunch to your employees for 3 hours and leave with no mess behind. Employees can order at the truck or skip the line and order online. All food is made to order from a gourmet kitchen on wheels.`}
          />
        </div>
      </section>

      <FeaturedTrucks
        title={`Meet ${city_name}'s Best Food ${vehicleType}s`}
        options={featuredTrucks}
      />

      <section className='Section--extraPadding'>
        <h2>A New Corporate Dining Solution</h2>
        <div className={tripleStyles.Triple_wrapper + ' ' + tripleStyles.Triple_wrapper__quad}>
          <Triple
            image='https://static.seattlefoodtruck.com/photos/services4.webp'
            isResponsive
            title={<><span aria-hidden='true'>🏆 </span>Retain &amp; Recruit Top Talent</>}
            text='Catered lunches are a top workplace perk that attract and keep top talent. Investing in better meals means stronger employee loyalty, increased productivity, and lower turnover costs.'
          />
          <Triple
            image='https://static.seattlefoodtruck.com/photos/services5.webp'
            isResponsive
            title={<><span aria-hidden='true'>⏳ </span>Boost Productivity</>}
            text='Win back valuable time by bringing top-tier food directly to your employees. Keeping great food on-site means fewer long lunch breaks, more collaboration, and a happier, more focused team.'
          />
          <Triple
            image='https://static.seattlefoodtruck.com/photos/services6.webp'
            isResponsive
            title={<><span aria-hidden='true'>🤝 </span>Foster a Stronger Workplace Community</>}
            text='Shared meals foster collaboration and teamwork by creating a sense of togetherness. Strengthen your team&#39;s connections and boost engagement with a dining experience designed for community.'
          />
          <Triple
            image='https://static.seattlefoodtruck.com/photos/services7.webp'
            isResponsive
            title={<><span aria-hidden='true'>🌍 </span>Support Local Businesses</>}
            text={`All our food ${vehicleType}s are owner-operated, prioritizing quality, variety, and sustainability. Treat your employees to flavors from around the world—without ever leaving the office!`}
          />
        </div>
      </section>

      <SiteRequirements />

      <section className='Section--extraPadding'>
        <div className={cn(styles.Cta, styles.Cta__reversed)}>
          <CtaImage image='https://static.seattlefoodtruck.com/photos/employee-subsidies.webp' isResponsive />
          <div className={styles.Cta_copy}>
            <h3>🍽️ Employee Meal Subsidization</h3>

            <p>Give your team a delicious perk without the hassle. Set a fixed dollar amount per employee, and we&#39;ll take care of the rest — seamless, flexible, and easy to manage.</p>

            <div>
              <Scroll.Link
                smooth
                to='form'
                className='Button Button--mobile-w100'
                offset={-50}
              >
                Learn more about subsidies
              </Scroll.Link>
            </div>
          </div>
        </div>
      </section>

      <Testimonials title='What People Are Saying' />

      <section className='Section--extraPadding'>
        <OfficeFaq />
      </section>

      <Scroll.Element name='form'>
        <div className='GrayBg'>
          <section className='Section--extraPadding'>
            <h2>Get in Touch — Bring Food {vehicle_type}s to Your Office!</h2>
            <ServicesForm formType='offices' />
          </section>
        </div>
      </Scroll.Element>
    </div>
  );
}

export default OfficeParks;
